// Single source of truth for US phone formatting.
//
// This lived in three places before: `shared/PhoneInput`, an inline copy in
// `MembersEdit`, and nowhere at all on the two public-facing forms, which
// accepted whatever the visitor typed. Masking as the user types is the one
// phone-field rule that matters most, so it belongs in one function every
// field reaches for.
//
// US-only by design: every lodge is a US Elks lodge, so there is no country
// selector and numbers are capped at 10 digits.

export const PHONE_DIGITS = 10

/** Strip everything that is not a digit, capped at a US number's length. */
export function phoneDigits(value: string | null | undefined): string {
  if (!value) return ''
  return value.replace(/\D/g, '').substring(0, PHONE_DIGITS)
}

/**
 * Format digits as `(555) 555-5555`, revealing punctuation only once the user
 * has typed far enough to need it. Partial input stays partial rather than
 * jumping ahead of the cursor.
 */
export function formatPhoneNumber(value: string | null | undefined): string {
  const digits = phoneDigits(value)
  if (digits.length === 0) return ''
  if (digits.length <= 3) return `(${digits}`
  if (digits.length <= 6) return `(${digits.substring(0, 3)}) ${digits.substring(3)}`
  return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`
}

/** True when the value holds a complete US number. Blank is not this function's concern. */
export function isCompletePhoneNumber(value: string | null | undefined): boolean {
  return phoneDigits(value).length === PHONE_DIGITS
}
